import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { validateAndRepairCurrentProfile } from '@/utils/profileNameMigration';
import { validatePersonalizationName } from '@/utils/nameContaminationBlocker';

interface ProfileData {
  first_name?: string;
  last_name?: string;
  email?: string;
  preferred_name?: string;
  name_usage_preference?: string;
  full_name?: string;
  biography?: string;
  avatar_url?: string;
  referral_code_id?: string;
  referral_applied_at?: string;
  referred_by?: string;

  // Personal demographic information
  age?: string;
  occupation?: string;
  relationship_status?: string;
  location?: string;
  hobbies?: string;
  stressors?: string;
  goals?: string;

  // Default session preferences
  default_voice_gender?: string;
  default_voice_type?: string;
  default_session_length?: number; // in seconds
  default_background_music?: string;
  default_environment?: string;

  // Category-specific preferences stored as JSONB
  category_preferences?: Record<string, any>;
}

export const useProfileManagement = () => {
  const { user, setAuthState } = useAuth();
  const { isDemoMode } = useDemo();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<ProfileData>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  const MAX_CHARACTERS = 3000;

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  useEffect(() => {
    const words = profile.biography?.trim() ? profile.biography.trim().split(/\s+/).length : 0;
    setWordCount(words);
  }, [profile.biography]);

  const loadProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Use backend API to fetch profile
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
        credentials: 'include' // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      setProfile(data);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast({
        title: "Error loading profile",
        description: error.message || 'Failed to load your profile',
        variant: "destructive",
      });

      // Set fallback profile from user data
      if (user) {
        setProfile({
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          full_name: user.full_name || '',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    // If user is in demo mode or not authenticated, redirect to create account
    if (!user || isDemoMode) {
      toast({
        title: "Create an account to save your profile",
        description: "Sign up to personalize your meditation experience and save your progress.",
        variant: "default",
      });
      navigate('/auth');
      return;
    }

    if (profile.biography && profile.biography.length > MAX_CHARACTERS) {
      toast({
        title: "Biography too long",
        description: `Please keep your biography under ${MAX_CHARACTERS} characters for optimal personalization.`,
        variant: "destructive",
      });
      return;
    }

    console.log('[PROFILE SAVE] Starting save process with data:', {
      profileData: profile,
      biographyLength: profile.biography?.length || 0,
      timestamp: new Date().toISOString()
    });

    setSaving(true);
    try {
      // Use backend API to update profile
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/profile`, {
        method: 'PUT',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save profile');
      }

      const data = await response.json();
      console.log('[PROFILE SAVE] Save successful:', data);

      // Update local state with saved data
      if (data.profile) {
        setProfile(data.profile);

        // CRITICAL: Update AuthContext to ensure all components see the new profile data
        // This prevents stale user data from being used in session generation
        setAuthState({
          ...user,
          first_name: data.profile.first_name,
          last_name: data.profile.last_name,
          full_name: data.profile.full_name,
          email: data.profile.email || user?.email,
        });

        // Invalidate any cached queries that depend on profile data
        // This ensures components using React Query will refetch fresh data
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.invalidateQueries({ queryKey: ['practitioner-profile'] });
        queryClient.invalidateQueries({ queryKey: ['user'] });

        console.log('[PROFILE SAVE] AuthContext and query caches updated');
      }

      toast({
        title: "✅ Profile saved successfully!",
        description: "Your profile information has been updated and is ready for personalized sessions.",
        duration: 4000,
        className: "bg-green-50 border-green-200 text-green-800",
      });
    } catch (error: any) {
      console.error('[PROFILE SAVE] Save process failed:', error);
      toast({
        title: "Error saving profile",
        description: error.message || 'Failed to save profile',
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!user) return;

    try {
      // Use the backend API to delete account
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/delete-account`, {
        method: 'DELETE',
        credentials: 'include' // Use cookie-based authentication
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Account deletion failed');
      }

      toast({
        title: "Account deleted",
        description: "Your account and all associated data have been permanently deleted.",
      });

      // Redirect to home page
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Error deleting account",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateProfile = (updates: Partial<ProfileData>) => {
    console.log('🔄 [PROFILE MANAGEMENT] updateProfile called with:', {
      updates,
      currentBiographyLength: profile.biography?.length || 0,
      newBiographyLength: updates.biography?.length || 0,
      timestamp: new Date().toISOString()
    });
    
    setProfile(prev => {
      const updated = { ...prev, ...updates };
      
      // 🚫 CRITICAL: Validate names for contamination before accepting updates
      if ('first_name' in updates && updates.first_name) {
        const validation = validatePersonalizationName(updates.first_name);
        if (!validation.isClean) {
          console.error('🚨 [PROFILE UPDATE BLOCKED] First name contains contaminated content:', updates.first_name);
          toast({
            title: "Invalid name",
            description: "The provided first name contains forbidden content.",
            variant: "destructive",
          });
          return prev; // Block the update
        }
      }
      
      if ('preferred_name' in updates && updates.preferred_name) {
        const validation = validatePersonalizationName(updates.preferred_name);
        if (!validation.isClean) {
          console.error('🚨 [PROFILE UPDATE BLOCKED] Preferred name contains contaminated content:', updates.preferred_name);
          toast({
            title: "Invalid name",
            description: "The provided preferred name contains forbidden content.",
            variant: "destructive",
          });
          return prev; // Block the update
        }
      }
      
      // Ensure biography is never undefined
      if ('biography' in updates && updates.biography !== undefined) {
        updated.biography = updates.biography.length > MAX_CHARACTERS 
          ? updates.biography.slice(0, MAX_CHARACTERS) 
          : updates.biography;
        
        console.log('📝 [PROFILE MANAGEMENT] Biography updated:', {
          finalBiographyLength: updated.biography.length,
          biographyPreview: updated.biography.substring(0, 100) + (updated.biography.length > 100 ? '...' : ''),
          timestamp: new Date().toISOString()
        });
      }
      
      // Handle name fields
      if ('first_name' in updates || 'last_name' in updates) {
        // Update full_name when first or last name changes
        const firstName = updates.first_name ?? updated.first_name ?? '';
        const lastName = updates.last_name ?? updated.last_name ?? '';
        updated.full_name = `${firstName} ${lastName}`.trim();
        
        console.log('👤 [PROFILE MANAGEMENT] Name fields updated:', {
          firstName,
          lastName,
          fullName: updated.full_name,
          timestamp: new Date().toISOString()
        });
      }
      
      return updated;
    });
  };

  // New function to validate and repair profile
  const validateAndRepairProfile = async () => {
    if (!user) return;
    
    console.log('🔍 [PROFILE VALIDATION] Starting profile validation and repair...');
    
    const result = await validateAndRepairCurrentProfile(user.id);
    
    if (result.repaired) {
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated to fix data consistency issues.",
        className: "bg-blue-50 border-blue-200 text-blue-800",
      });
      await loadProfile(); // Reload the repaired profile
    }
    
    if (!result.isValid && result.issues.length > 0) {
      console.warn('⚠️ [PROFILE VALIDATION] Issues found:', result.issues);
      toast({
        title: "Profile Issues Detected",
        description: result.issues.join(', '),
        variant: "destructive",
      });
    }
    
    return result;
  };

  return {
    profile,
    loading,
    saving,
    wordCount,
    maxCharacters: MAX_CHARACTERS,
    loadProfile,
    saveProfile,
    deleteAccount,
    updateProfile,
    validateAndRepairProfile,
  };
};